import { useEffect, useState, useRef } from "react";
import { FaSmile, FaMeh, FaFrown } from "react-icons/fa";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useBoletin } from "../../context/BoletinContext";
import { useUsers } from "../../context/UsersContext";

const BoletinForm = ({ students, periodos, showNotification, onBoletinGuardado, editingBoletin, clearEditing }) => {
  const { saveBoletin, actualizarBoletin, loading } = useBoletin();
  const { salonGrado, fetchSalonForDirector } = useUsers();

  // ✅ USAR REF para controlar ReactQuill directamente
  const quillRef = useRef(null);

  const [formData, setFormData] = useState({
    students_id: "",
    periodo_id: "",
    observacion: "",
    cumplimiento_tareas: "",
    comportamiento_valores: "",
    comportamiento_numerico: "",
    inasistencias: "",
  });

  const [searchValue, setSearchValue] = useState("");
  const [filteredStudents, setFilteredStudents] = useState([]);

  useEffect(() => {
    fetchSalonForDirector();
  }, []);

  // ✅ CARGAR DATOS AL EDITAR usando el editor de Quill directamente
  useEffect(() => {
    if (editingBoletin && students.length > 0) {
      const estudiante = students.find(s => s.user_id === editingBoletin.students_id);
  
      setFormData({
        students_id: editingBoletin.students_id,
        periodo_id: editingBoletin.periodo_id,
        observacion: editingBoletin.observacion || "",
        cumplimiento_tareas: editingBoletin.cumplimiento_tareas || "",
        comportamiento_valores: editingBoletin.comportamiento_valores || "",
        comportamiento_numerico: editingBoletin.comportamiento_numerico || "",
        inasistencias: editingBoletin.inasistencias || "",
      });

      // ✅ FORZAR CARGA EN QUILL usando el editor interno
      if (quillRef.current) {
        const editor = quillRef.current.getEditor();
        const contenido = editingBoletin.observacion || "";
        editor.clipboard.dangerouslyPasteHTML(contenido);
      }
  
      if (estudiante) {
        setSearchValue(
          `${estudiante.first_name} ${estudiante.second_name || ""} ${estudiante.first_surname || ""} ${estudiante.second_surname || ""}`.trim()
        );
      }
    } else {
      // Limpiar formulario
      setFormData({
        students_id: "",
        periodo_id: "",
        observacion: "",
        cumplimiento_tareas: "",
        comportamiento_valores: "",
        comportamiento_numerico: "",
        inasistencias: "",
      });
      setSearchValue("");

      // ✅ Limpiar editor de Quill
      if (quillRef.current) {
        const editor = quillRef.current.getEditor();
        editor.setText("");
      }
    }
  }, [editingBoletin, students]);
  
  
  const grado = salonGrado?.salon_grado?.toLowerCase();
  const esPreescolar = ["parvulos", "prejardin", "jardin", "transicion"].includes(grado);

  const opcionesEstado = [
    {
      value: "feliz",
      icon: <FaSmile size={40} />,
      color: "text-green",
    },
    {
      value: "contento",
      icon: <FaSmile size={40} />,
      color: "text-main-blue",
    },
    {
      value: "intermedio",
      icon: <FaMeh size={40} />,
      color: "text-yellow-500",
    },
    {
      value: "triste",
      icon: <FaFrown size={40} />,
      color: "text-red-500",
    },
  ];

  const handleStudentSearch = (e) => {
    const searchTerm = e.target.value.toLowerCase();
    setSearchValue(searchTerm);

    if (searchTerm.length > 0) {
      const results = students.filter(
        (student) =>
          student.user_id.toString().includes(searchTerm) ||
          `${student.first_name} ${student.second_name} ${student.first_surname} ${student.second_surname}`
            .toLowerCase()
            .includes(searchTerm)
      );
      setFilteredStudents(results);
    } else {
      setFilteredStudents([]);
    }
  };

  const handleStudentSelect = (student) => {
    setFormData((prev) => ({
      ...prev,
      students_id: student.user_id,
    }));
    setSearchValue(
      `${student.first_name} ${student.second_name || ""} ${
        student.first_surname || ""} ${student.second_surname || ""
      }`.trim()
    );
    setFilteredStudents([]);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleQuillChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      observacion: value === "<p><br></p>" ? "" : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const {
      students_id,
      periodo_id,
      observacion,
      inasistencias,
      comportamiento_numerico,
      cumplimiento_tareas,
      comportamiento_valores,
    } = formData;

    const camposValidos = esPreescolar
      ? cumplimiento_tareas && comportamiento_valores
      : comportamiento_numerico;

    if (
      !students_id ||
      !periodo_id ||
      !observacion ||
      !inasistencias ||
      !camposValidos
    ) {
      showNotification({
        type: "error",
        message: "Por favor, complete todos los campos.",
      });
      return;
    }

    try {
      const response = editingBoletin
        ? await actualizarBoletin(formData)
        : await saveBoletin(formData);

      showNotification({
        type: "success",
        message: response.message,
      });
    
      if (onBoletinGuardado) {
        onBoletinGuardado();
      }

      // Limpiar formulario
      setFormData({
        students_id: "",
        periodo_id: "",
        observacion: "",
        cumplimiento_tareas: "",
        comportamiento_valores: "",
        comportamiento_numerico: "",
        inasistencias: "",
      });
      setSearchValue("");

      // ✅ Limpiar editor
      if (quillRef.current) {
        const editor = quillRef.current.getEditor();
        editor.setText("");
      }
      
      if (editingBoletin) clearEditing?.();

    } catch (error) {
      const data = error.response?.data;
      const faltantes = data?.detalles?.asignaturasFaltantes;
      const message = faltantes?.length
        ? `${data.message}\n\nAsignatura(s) sin nota: ${faltantes.join(", ")}`
        : data?.message || "Error al guardar boletín.";
      showNotification({ type: "error", message });
    }
  };

  if (!grado) {
    return <p className="p-4 text-gray-700">Cargando información del salón...</p>;
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-white p-6 rounded-lg shadow-md overflow-x-auto"
    >
      <h3 className="text-lg font-semibold mb-4">
        {editingBoletin ? "Editar Boletín" : "Agregar Boletín"}
      </h3>

      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2 relative">
          <input
            type="text"
            placeholder="Buscar estudiante por ID o nombre"
            value={searchValue}
            onChange={handleStudentSearch}
            className="w-full p-2 border border-gray-300 rounded"
            required
            disabled={!!editingBoletin}
          />
          {filteredStudents.length > 0 && (
            <ul className="absolute z-10 bg-white border border-gray-300 rounded shadow-md max-h-40 overflow-y-auto w-full">
              {filteredStudents.map((student) => (
                <li
                  key={student.user_id}
                  className="p-2 hover:bg-gray-100 cursor-pointer"
                  onClick={() => handleStudentSelect(student)}
                >
                  {student.user_id} - {student.first_name} {student.second_name}{" "}
                  {student.first_surname} {student.second_surname}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div>
          <label className="block">Periodo</label>
          <select
            name="periodo_id"
            value={formData.periodo_id}
            onChange={handleInputChange}
            className="w-full p-2 border rounded-lg"
            disabled={!!editingBoletin}
            required
          >
            <option value="">Seleccione un periodo</option>
            {periodos
              .filter(p => p.nombre.toLowerCase() !== "periodo final")
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nombre}
                </option>
              ))}
          </select>
        </div>

        <div>
          <label className="block">Inasistencias</label>
          <input
            type="number"
            name="inasistencias"
            value={formData.inasistencias}
            onChange={handleInputChange}
            className="w-full p-2 border rounded-lg"
            required
          />
        </div>

        {esPreescolar ? (
          <>
            <div>
              <label className="block mb-2">Cumplimiento de Tareas</label>
              <div className="flex flex-wrap gap-2">
                {opcionesEstado.map(({ value, icon, color }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        cumplimiento_tareas: value,
                      }))
                    }
                    className={`p-2 ${formData.cumplimiento_tareas === value ? "bg-blue-200 rounded-2xl" : ""}`}
                  >
                    <span className={`text-3xl ${color}`}>{icon}</span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block mb-2">Comportamiento</label>
              <div className="flex flex-wrap gap-2">
                {opcionesEstado.map(({ value, icon, color }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        comportamiento_valores: value,
                      }))
                    }
                    className={`p-2 ${formData.comportamiento_valores === value ? "bg-blue-200 rounded-2xl" : ""}`}
                  >
                    <span className={`text-3xl ${color}`}>{icon}</span>
                  </button>
                ))}
              </div>
            </div>
          </>
        ) : (
          <div className="col-span-2">
            <label className="block mb-2">Comportamiento (1.0 a 5.0)</label>
            <input
              type="number"
              name="comportamiento_numerico"
              min="1"
              max="5"
              step="0.1"
              value={formData.comportamiento_numerico}
              onChange={handleInputChange}
              className="w-full p-2 border rounded-lg"
              required
            />
          </div>
        )}

        <div className="col-span-2">
          <label className="block mb-2">Observación</label>
          <ReactQuill
            ref={quillRef}
            value={formData.observacion}
            onChange={handleQuillChange}
            className="bg-white rounded-lg"
            theme="snow"
            placeholder="Escribe la observación aquí..."
          />
        </div>
      </div>

      <div className="flex justify-start gap-6 mt-4">
        <button
          type="submit"
          className="bg-blue-800 text-white px-4 py-2 rounded-lg"
          disabled={loading}
        >
          {loading ? "Guardando..." : editingBoletin ? "Actualizar Boletín" : "Guardar Boletín"}
        </button>

        {editingBoletin && (
          <button
            type="button"
            className="bg-red-700 text-white px-4 py-2 rounded-lg"
            onClick={() => {
              setFormData({
                students_id: "",
                periodo_id: "",
                observacion: "",
                cumplimiento_tareas: "",
                comportamiento_valores: "",
                comportamiento_numerico: "",
                inasistencias: "",
              });
              setSearchValue("");
              
              if (quillRef.current) {
                const editor = quillRef.current.getEditor();
                editor.setText("");
              }
              
              clearEditing();
            }}
          >
            Cancelar Actualización
          </button>
        )}
      </div>
    </form>
  );
};

export default BoletinForm;